'use client';

import { AppleLogo } from '@/app_components/icons/logos/AppleLogo';
import { FacebookLogo } from '@/app_components/icons/logos/FacebookLogo';
import { GoogleLogo } from '@/app_components/icons/logos/GoogleLogo';
import { SocialSignInButton } from '@/app_components/SignIn/SSO/SocialSignInButton';
import { type SocialLoginTypeError, SocialType, type SocialUrls } from '@/app_lib/types/accounts/signin';

const socialProviders = [
  { label: 'Google', logo: GoogleLogo, type: SocialType.Google },
  { label: 'Apple', logo: AppleLogo, type: SocialType.Apple },
  { label: 'Facebook', logo: FacebookLogo, type: SocialType.Facebook },
] as const;

interface SocialSignInContainerProps {
  socialUrls: SocialUrls;
  socialLoginLoading: boolean;
  socialLoginTypeError: SocialLoginTypeError | null;
  onLoadingChange: (loading: boolean) => void;
  disableSocialLogins: boolean;
}

const getErrorMessageText = (socialLoginTypeError: SocialLoginTypeError) => {
  const unknownType = 'There was an error logging you in.';
  const knownType = `There was an error logging you in with ${socialLoginTypeError.charAt(0).toUpperCase() + socialLoginTypeError.slice(1)}.`;
  const baseText = 'Please try again or use another method.';
  return socialLoginTypeError === 'Unknown' ? `${unknownType} ${baseText}` : `${knownType} ${baseText}`;
};

export const SocialSignInContainer = ({
  disableSocialLogins,
  socialUrls,
  socialLoginLoading,
  socialLoginTypeError,
  onLoadingChange,
}: SocialSignInContainerProps) => {
  return (
    <div className='w-full'>
      {socialLoginTypeError && (
        <div className='text-red pb-6 text-center'>{getErrorMessageText(socialLoginTypeError)}</div>
      )}
      <div className='flex flex-col justify-center items-center gap-4 self-stretch'>
        {socialProviders.map(({ type, label, logo: Logo }) => {
          const url = socialUrls[type];
          if (!url) {
            return null;
          }
          return (
            <SocialSignInButton
              disabled={socialLoginLoading || disableSocialLogins}
              key={type}
              onLoadingChange={onLoadingChange}
              socialType={type}
              url={url}
            >
              <Logo />
              Continue with {label}
            </SocialSignInButton>
          );
        })}
      </div>
    </div>
  );
};
